import { Skeleton } from '@/components/loading/Skeleton'
import { useMemo, type ReactNode } from 'react'
import { VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { Signal, Video, VideoOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEventLiveKit, useEventLiveKitTrackRefs } from './EventLiveKitProvider'
import { useStreamLiveStatus } from '@/hooks/useStreamLiveStatus'
import { deriveStreamPresenceState } from '@/lib/streamPresence'
import type { StreamRouteStatus, StreamStatus } from '@/types/events'

type StreamVideoSurfaceProps = {
  participantIdentity?: string | null
  status?: StreamStatus
  routeStatus?: StreamRouteStatus | null
  muted?: boolean
  className?: string
  fallback?: ReactNode
  overlay?: ReactNode
}

export function StreamVideoSurface({ participantIdentity, status, routeStatus, muted = true, className, fallback, overlay }: StreamVideoSurfaceProps) {
  const { enabled } = useEventLiveKit()
  const { hasTrack } = useStreamLiveStatus(participantIdentity, status)

  const fallbackContent = fallback ?? <DefaultFallback status={status} routeStatus={routeStatus} hasTrack={hasTrack} />

  if (!enabled || !participantIdentity) {
    return (
      <div className={cn('relative h-full w-full overflow-hidden rounded-xl bg-neutral-10', className)}>
        <div className="grid h-full w-full place-items-center text-neutral-5">{fallbackContent}</div>
        {overlay}
      </div>
    )
  }

  return (
    <StreamVideoSurfaceConnected
      participantIdentity={participantIdentity}
      muted={muted}
      className={className}
      fallback={fallbackContent}
      overlay={overlay}
    />
  )
}

function StreamVideoSurfaceConnected({
  participantIdentity,
  muted = true,
  className,
  fallback,
  overlay,
}: Required<Pick<StreamVideoSurfaceProps, 'participantIdentity'>> & Omit<StreamVideoSurfaceProps, 'participantIdentity'>) {
  const trackRefs = useEventLiveKitTrackRefs()

  const primaryTrack = useMemo(() => {
    const camera = trackRefs.find((ref) => ref.participant.identity === participantIdentity && ref.source === Track.Source.Camera)
    if (camera) return camera
    return trackRefs.find((ref) => ref.participant.identity === participantIdentity)
  }, [trackRefs, participantIdentity])

  return (
    <div className={cn('relative h-full w-full overflow-hidden rounded-xl bg-black', className)}>
      {primaryTrack ? (
        <VideoTrack
          trackRef={primaryTrack}
          playsInline
          muted={muted}
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="grid h-full w-full place-items-center bg-neutral-10 text-neutral-5">{fallback}</div>
      )}
      {overlay}
    </div>
  )
}

function DefaultFallback({
  status,
  routeStatus,
  hasTrack = false,
}: {
  status?: StreamStatus
  routeStatus?: StreamRouteStatus | null
  hasTrack?: boolean
}) {
  const presence = deriveStreamPresenceState(status, hasTrack, routeStatus)

  if (['starting', 'connecting', 'reconnecting'].includes(presence.phase)) {
    return <div role="status" aria-label={`${presence.label} camera preview`} aria-busy="true" className="absolute inset-0"><Skeleton className="h-full w-full rounded-none bg-white/10" /></div>
  }

  if (presence.phase === 'waiting') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-neutral-9 text-neutral-6 transition-all duration-300">
          <VideoOff className="h-5 w-5" />
        </div>
        <span className="text-size-xs font-medium text-neutral-5">
          No video yet
        </span>
      </div>
    )
  }
  if (presence.phase === 'ready') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-neutral-9 text-neutral-5 transition-all duration-300">
          <Video className="h-5 w-5" />
        </div>
        <span className="text-size-xs font-medium text-neutral-5">
          Ready
        </span>
      </div>
    )
  }
  if (presence.phase === 'stopping') {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-warning text-neutral-1 transition-all duration-300">
          <Signal className="h-5 w-5" />
        </div>
        <span className="text-size-xs font-medium text-neutral-5">
          Stopping stream
        </span>
      </div>
    )
  }

  if (presence.phase === 'error') {
    return (
      <div className="flex flex-col items-center gap-2">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-error text-neutral-1 transition-all duration-500">
          <Signal className="h-5 w-5" />
        </span>
        <span className="text-size-xs font-medium text-neutral-5">
          Stream error
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-neutral-9 text-neutral-6 transition-all duration-300">
        <Video className="h-5 w-5" />
      </div>
    </div>
  )
}
